import { useCallback, useEffect, useRef, useState } from "react";
import { apiError } from "./api";

/**
 * Admin data loading that can never make loaded data look like it disappeared.
 *
 * Why this exists: every admin page used to be
 *     api.get("/admin/x").then(({ data }) => setX(data.x))
 * with no error branch. When the request failed (expired session, network drop,
 * backend 5xx) nothing was set, so a freshly mounted page rendered its initial
 * `[]` — the catalogue appeared to be empty — and a failed refresh silently
 * looked like "no data".
 *
 * Rules implemented here:
 *  - The last SUCCESSFUL payload is never replaced by an empty array / zero
 *    counts because a request failed.
 *  - A genuine empty result from the backend is the only thing that produces an
 *    empty state (`data` becomes `[]`/`0` and `error` stays null).
 *  - A failed refresh keeps the old data on screen, sets `error`, and marks the
 *    payload as `stale` so it is never presented as freshly updated.
 *  - `reload` never rejects, so callers can await it without unhandled promise
 *    rejections.
 */
export default function useAdminData(fetcher, { select } = {}) {
  const [data, setData] = useState(undefined); // undefined = never loaded
  const [loading, setLoading] = useState(true); // first request still in flight
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const fetcherRef = useRef(fetcher);
  const selectRef = useRef(select);
  const dataRef = useRef(undefined);
  const requestId = useRef(0);

  useEffect(() => { fetcherRef.current = fetcher; }, [fetcher]);
  useEffect(() => { selectRef.current = select; }, [select]);

  const reload = useCallback(async () => {
    const id = ++requestId.current;
    setRefreshing(true);
    try {
      const raw = await fetcherRef.current();
      const payload = selectRef.current ? selectRef.current(raw) : raw;
      if (id !== requestId.current) return { ok: true, data: payload, superseded: true };
      dataRef.current = payload;
      setData(payload);
      setLastLoadedAt(new Date());
      setError(null);
      return { ok: true, data: payload };
    } catch (e) {
      const message = apiError(e);
      if (id === requestId.current) {
        // Deliberately keeps `data` untouched: last known good data stays visible.
        setError({ message, status: e?.response?.status ?? null, at: new Date() });
      }
      return { ok: false, error: e, message };
    } finally {
      if (id === requestId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  // Replace the in-memory payload (e.g. right after a successful save) without a
  // round trip. Never used to write "empty" on failure.
  const replace = useCallback((updater) => {
    const next = typeof updater === "function" ? updater(dataRef.current) : updater;
    dataRef.current = next;
    setData(next);
  }, []);

  return {
    data,
    hasData: data !== undefined,
    loading,
    refreshing,
    error,
    stale: !!error && data !== undefined,
    lastLoadedAt,
    reload,
    replace,
  };
}